/**
 * Typed API client.
 *
 * Types mirror backend/app/schemas.py. Access tokens live in memory plus
 * localStorage; a 401 triggers one silent refresh attempt before the caller sees
 * an error, so a long map session does not get interrupted by token expiry.
 */

export type Level = 'N5' | 'N4' | 'N3' | 'N2' | 'N1'
export type QuizMode = 'meaning' | 'reading' | 'component' | 'kanji_from_meaning' | 'mixed'
export type Grade = 'again' | 'hard' | 'good' | 'easy'
export type Status = 'learning' | 'review' | 'mastered'

export const LEVELS: Level[] = ['N5', 'N4', 'N3', 'N2', 'N1']

export interface User {
  id: number
  email: string
  display_name: string
  target_level: Level
  daily_goal: number
  avatar_seed: string
  created_at: string
  last_login_at: string | null
}

export interface Tokens {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
}

export interface KanjiNode {
  char: string
  level: Level | null
  meaning: string
  romaji: string
  strokes: number | null
  is_kanji: boolean
  is_radical: boolean
  child_count: number
  parent_count: number
  example_count: number
  depth: number
  status: Status | null
  in_flashcards: boolean
}

export interface Component {
  char: string
  meaning: string
  romaji: string
  position: number
  level: Level | null
  is_kanji: boolean
  strokes: number | null
}

export interface Example {
  word: string
  reading: string
  romaji: string
  meaning: string
}

export interface KanjiDetail {
  char: string
  level: Level | null
  meanings: string[]
  on_readings: string[]
  kun_readings: string[]
  on_romaji: string[]
  kun_romaji: string[]
  strokes: number | null
  freq: number | null
  grade: number | null
  radical_no: number | null
  radical_name: string | null
  radical_kana: string | null
  is_kanji: boolean
  is_radical: boolean
  components: Component[]
  used_in: KanjiNode[]
  examples: Example[]
  status: Status | null
  in_flashcards: boolean
  note: string | null
}

export interface GraphEdge {
  source: string
  target: string
  position: number
}

export interface Graph {
  nodes: KanjiNode[]
  edges: GraphEdge[]
  roots: string[]
  truncated: boolean
  stats: Record<string, number>
}

export interface Branch {
  char: string
  meaning: string
  romaji: string
  family: number
}

export interface LevelStat {
  level: Level
  total: number
  seen: number
  mastered: number
}

export interface Overview {
  levels: LevelStat[]
  total_kanji: number
  total_components: number
  total_radicals: number
  total_edges: number
}

export interface Progress {
  char: string
  status: Status
  ease: number
  interval_days: number
  due_at: string | null
  reps: number
  lapses: number
  streak: number
  last_seen_at: string | null
  mastered_at: string | null
}

export interface ReviewQueue {
  due: KanjiNode[]
  new: KanjiNode[]
  due_count: number
  new_count: number
}

export interface QuizQuestion {
  position: number
  char: string | null
  prompt: string
  prompt_type: string
  hint: string | null
  options: { key: string; text: string }[]
}

export interface Quiz {
  quiz_id: number
  level: Level
  mode: QuizMode
  total: number
  questions: QuizQuestion[]
}

export interface QuizResultItem {
  position: number
  char: string
  prompt: string
  correct: boolean
  answer: string | null
  expected: string
  elapsed_ms: number
}

export interface QuizResult {
  quiz_id: number
  score: number
  total: number
  accuracy: number
  duration_ms: number
  passed: boolean
  items: QuizResultItem[]
  level_up_suggested: boolean
}

export interface QuizReview {
  quiz_id: number
  level: Level
  mode: QuizMode
  score: number
  total: number
  accuracy: number
  duration_ms: number
  passed: boolean
  started_at: string
  finished_at: string | null
  items: QuizResultItem[]
}

export interface Scorecard {
  user: User
  levels: LevelStat[]
  total_seen: number
  total_mastered: number
  mastery_pct: number
  quizzes_taken: number
  questions_answered: number
  overall_accuracy: number
  best_accuracy: number
  current_streak_days: number
  longest_streak_days: number
  flashcards: number
  due_today: number
  recent_quizzes: Record<string, string | number>[]
  accuracy_by_mode: Record<string, number>
}

export interface LeaderRow {
  rank: number
  user_id: number
  display_name: string
  avatar_seed: string
  target_level: Level
  mastered: number
  accuracy: number
  quizzes: number
  points: number
  is_me: boolean
}

export interface Leaderboard {
  rows: LeaderRow[]
  me: LeaderRow | null
  metric: string
}

// --------------------------------------------------------------- token storage
const ACCESS_KEY = 'nv.access'
const REFRESH_KEY = 'nv.refresh'

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  save(t: Tokens) {
    localStorage.setItem(ACCESS_KEY, t.access_token)
    localStorage.setItem(REFRESH_KEY, t.refresh_token)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
    this.name = 'ApiError'
  }
}

const BASE = '/api'

/** Pull a readable message out of FastAPI's several error shapes. */
async function errorMessage(res: Response): Promise<string> {
  try {
    const body = await res.json()
    const d = body?.detail
    if (typeof d === 'string') return d
    if (Array.isArray(d)) {
      return d
        .map((e) => {
          const field = Array.isArray(e.loc) ? e.loc.filter((x: unknown) => x !== 'body').join('.') : ''
          return field ? `${field}: ${e.msg}` : e.msg
        })
        .join('; ')
    }
    if (d) return JSON.stringify(d)
  } catch {
    /* not JSON */
  }
  return res.statusText || `request failed (${res.status})`
}

let refreshing: Promise<boolean> | null = null

/** Exchange the refresh token for a new pair. Concurrent callers share one call. */
async function tryRefresh(): Promise<boolean> {
  const token = tokenStore.refresh
  if (!token) return false
  if (!refreshing) {
    refreshing = (async () => {
      try {
        const res = await fetch(`${BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: token }),
        })
        if (!res.ok) return false
        tokenStore.save(await res.json())
        return true
      } catch {
        return false
      } finally {
        refreshing = null
      }
    })()
  }
  return refreshing
}

interface RequestOptions {
  method?: string
  body?: unknown
  auth?: boolean
  retry?: boolean
  signal?: AbortSignal
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, auth = true, retry = true, signal } = opts
  const headers: Record<string, string> = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = tokenStore.access
  if (auth && token) headers.Authorization = `Bearer ${token}`

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  })

  if (res.status === 401 && auth && retry && tokenStore.refresh) {
    if (await tryRefresh()) return request<T>(path, { ...opts, retry: false })
    tokenStore.clear()
  }
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res))
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const qs = (params: Record<string, string | number | boolean | null | undefined>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== null && v !== undefined && v !== '') sp.set(k, String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  // auth
  register: (b: { email: string; password: string; display_name: string; target_level?: Level }) =>
    request<{ user: User; tokens: Tokens }>('/auth/register', { method: 'POST', body: b, auth: false }),
  login: (b: { email: string; password: string }) =>
    request<{ user: User; tokens: Tokens }>('/auth/login', { method: 'POST', body: b, auth: false }),
  me: () => request<User>('/me'),
  updateMe: (b: Partial<Pick<User, 'display_name' | 'target_level' | 'daily_goal' | 'avatar_seed'>>) =>
    request<User>('/me', { method: 'PATCH', body: b }),
  changePassword: (b: { current_password: string; new_password: string }) =>
    request<void>('/me/password', { method: 'POST', body: b }),

  // kanji + map
  overview: () => request<Overview>('/kanji/overview'),
  graph: (p: { level?: Level | null; depth?: number; branch?: string | null; include_strokes?: boolean }) =>
    request<Graph>(`/kanji/graph${qs(p)}`),
  branches: (level?: Level | null, limit = 80) => request<Branch[]>(`/kanji/branches${qs({ level, limit })}`),
  detail: (char: string) => request<KanjiDetail>(`/kanji/${encodeURIComponent(char)}`),
  expand: (char: string, depth = 1) =>
    request<Graph>(`/kanji/${encodeURIComponent(char)}/expand${qs({ depth })}`),
  usedIn: (char: string, level?: Level | null, limit = 200) =>
    request<KanjiNode[]>(`/kanji/${encodeURIComponent(char)}/used-in${qs({ level, limit })}`),
  search: (q: string, limit = 25) => request<KanjiNode[]>(`/kanji/search${qs({ q, limit })}`),

  // progress
  grade: (char: string, grade: Grade) =>
    request<Progress>('/progress/grade', { method: 'POST', body: { char, grade } }),
  setMastered: (char: string, mastered = true) =>
    request<Progress>('/progress/master', { method: 'POST', body: { char, mastered } }),
  progress: (p: { level?: Level; status?: Status } = {}) => request<Progress[]>(`/progress${qs(p)}`),
  queue: (level?: Level, limit = 20) => request<ReviewQueue>(`/progress/queue${qs({ level, limit })}`),

  // notes + flashcards
  saveNote: (char: string, body: string) =>
    request<{ char: string; body: string | null }>('/notes', { method: 'PUT', body: { char, body } }),
  flashcards: () => request<KanjiNode[]>('/flashcards'),
  addFlashcard: (char: string) =>
    request<{ char: string; count: number }>('/flashcards', { method: 'POST', body: { char } }),
  removeFlashcard: (char: string) =>
    request<{ char: string; count: number }>(`/flashcards/${encodeURIComponent(char)}`, { method: 'DELETE' }),

  // quiz
  startQuiz: (b: { level: Level; mode: QuizMode; count: number; only_due?: boolean }) =>
    request<Quiz>('/quiz/start', { method: 'POST', body: b }),
  submitQuiz: (b: {
    quiz_id: number
    answers: { position: number; answer: string; elapsed_ms: number }[]
    duration_ms: number
  }) => request<QuizResult>('/quiz/submit', { method: 'POST', body: b }),
  quizHistory: (limit = 20) => request<Record<string, string | number>[]>(`/quiz/history${qs({ limit })}`),
  quizReview: (quizId: number) => request<QuizReview>(`/quiz/${quizId}`),

  // scores
  scorecard: () => request<Scorecard>('/scores/me'),
  leaderboard: (metric: 'points' | 'mastered' | 'accuracy' = 'points', limit = 25) =>
    request<Leaderboard>(`/scores/leaderboard${qs({ metric, limit })}`),
}

/** Tailwind colour per JLPT level, shared by badges, map nodes and charts. */
export const levelColor: Record<Level, string> = {
  N5: '#22c55e',
  N4: '#3b82f6',
  N3: '#06b6d4',
  N2: '#ec4899',
  N1: '#f59e0b',
}

export const levelRing: Record<Level, string> = {
  N5: 'ring-n5/60 text-n5',
  N4: 'ring-n4/60 text-n4',
  N3: 'ring-n3/60 text-n3',
  N2: 'ring-n2/60 text-n2',
  N1: 'ring-n1/60 text-n1',
}
